'use client';
import type { ReactNode } from 'react';
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import type { CredentialPreviewDto } from '@terra/shared-contracts-applications';

type ChatCredentialsContextType = {
  credentialsPreview: CredentialPreviewDto[];
  setCredentialsPreview: (credentials: CredentialPreviewDto[]) => void;
};

const ChatCredentialsContext = createContext<
  ChatCredentialsContextType | undefined
>(undefined);

type ChatCredentialsProviderProps = {
  children: ReactNode;
  initialCredentialsPreview?: CredentialPreviewDto[];
};

export function ChatCredentialsProvider({
  children,
  initialCredentialsPreview = [],
}: ChatCredentialsProviderProps) {
  const [credentialsPreview, setCredentialsPreviewState] = useState<
    CredentialPreviewDto[]
  >(initialCredentialsPreview);

  const setCredentialsPreview = useCallback(
    (credentials: CredentialPreviewDto[]) => {
      setCredentialsPreviewState(credentials);
    },
    [],
  );

  const contextValue: ChatCredentialsContextType = useMemo(
    () => ({
      credentialsPreview,
      setCredentialsPreview,
    }),
    [credentialsPreview, setCredentialsPreview],
  );

  return (
    <ChatCredentialsContext.Provider value={contextValue}>
      {children}
    </ChatCredentialsContext.Provider>
  );
}

export function useCredentialsContext() {
  const context = useContext(ChatCredentialsContext);
  if (context === undefined) {
    throw new Error(
      'useCredentialsContext must be used within a ChatCredentialsProvider',
    );
  }
  return context;
}

export function useOptionalCredentialsContext():
  | ChatCredentialsContextType
  | undefined {
  return useContext(ChatCredentialsContext);
}
